<script setup>
import { useRoute, useRouter } from 'vue-router';
import { useDirection } from '@/composables/useDirection';
import { useLanguageStore } from '@/stores/language';
import { computed } from 'vue';
import { ChevronRight, ChevronLeft } from 'lucide-vue-next';

const route = useRoute();
const router = useRouter();
const { spacing, isRtl } = useDirection();
const { t } = useLanguageStore();

// Check if we're on the home page
const isHomePage = computed(() => route.path === '/');

// Compute breadcrumb items with proper structure
const breadcrumbItems = computed(() => {
    if (!route.meta.breadcrumb || route.meta.breadcrumb.length === 0) {
        return [];
    }

    
    
    // Filter out 'Home' from breadcrumb items
    const filteredBreadcrumb = route.meta.breadcrumb.filter(label => label.toLowerCase() !== 'home');
    
    return filteredBreadcrumb.map((label, index) => ({
        label,
        index,
        isLast: index === filteredBreadcrumb.length - 1,
        path: null
    }));
});

// Handle breadcrumb click
const handleBreadcrumbClick = (item) => {
    if (item.path && !item.isLast) {
        router.push(item.path);
    }
};
</script>

<template>
    <div v-if="isHomePage" class="home-breadcrumb-brand flex items-center gap-2">
        <span class="home-breadcrumb-brand__mark" aria-hidden="true"></span>
        <span class="home-breadcrumb-brand__label">{{ isRtl ? 'الخدمات الطبية بوزارة الداخلية' : 'MOI Medical Services' }}</span>
    </div>
    
    <!-- Show breadcrumbs WITHOUT home icon for all pages with breadcrumb array -->
    <div v-else-if="breadcrumbItems.length > 0" :class="['flex items-center gap-2']">
        <!-- Breadcrumb Items Only -->
        <template v-for="(item, idx) in breadcrumbItems" :key="item.index">
            <div class="flex items-center gap-2">
                <!-- Separator (only between items, not before first) -->
                <ChevronLeft 
                    v-if="idx > 0 && isRtl"
                    :size="14" 
                    aria-hidden="true"
                    class="text-surface-300 dark:text-surface-600" 
                />
                <ChevronRight 
                    v-if="idx > 0 && !isRtl"
                    :size="14" 
                    aria-hidden="true"
                    class="text-surface-300 dark:text-surface-600" 
                />
                
                <!-- Breadcrumb Label -->
                <span 
                    :class="[
                        'leading-none text-sm transition-colors duration-200',
                        item.isLast 
                            ? 'text-surface-950 dark:text-surface-0 font-semibold' 
                            : 'text-surface-500 dark:text-surface-400 font-normal hover:text-surface-900 dark:hover:text-surface-100',
                        item.path && !item.isLast ? 'cursor-pointer' : ''
                    ]"
                    @click="handleBreadcrumbClick(item)"
                >
                    {{ t(item.label) }}
                </span>
            </div>
        </template>
    </div>
    
    <!-- Fallback when no breadcrumb is defined - just show the page title -->
    <div v-else class="flex items-center gap-2">
        <span class="text-sm font-semibold text-surface-950 dark:text-surface-0 leading-none">
            {{ t(route.meta.title || 'Dashboard') }}
        </span>
    </div>
</template>

<style scoped>
.home-breadcrumb-brand {
    display: inline-flex;
    min-height: 2rem;
    align-items: center;
    justify-content: center;
    color: var(--sfh-teal-dark, #168c91);
    font-size: .95rem;
    font-weight: 800;
    line-height: normal;
    letter-spacing: -.01em;
}

.home-breadcrumb-brand__mark {
    display: block;
    flex: 0 0 auto;
    width: .65rem;
    height: .65rem;
    border: 2px solid var(--sfh-surface-card, #fff);
    border-radius: 50%;
    background: var(--sfh-gold, #b59a63);
    box-shadow: 0 0 0 3px rgba(181, 154, 99, .2);
}

.home-breadcrumb-brand__label {
    display: block;
    position: relative;
    top: 1px;
    margin: 0;
    padding: 0;
    line-height: 1;
    text-align: center;
}
</style>
